import * as t from "io-ts";
import {Exclude, Expose, instanceToPlain, plainToInstance, Type} from "class-transformer";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Warning, WarningArrayValidator} from "./warning";
import {PersonEntity, PersonValidator} from "./person";
import {ShiftEntity, ShiftValidator} from "./shift";
import {TransportEntity, TransportValidator} from "./transport";
import {TransportAssocEntity, TransportAssocValidator} from "./transport-assoc";
import {GeofenceEventEntity, GeofenceEventValidator} from "./geofence-event";
import {DispatchEntity, DispatchValidator} from "./dispatch";
import {ContainerVolumeDropEntity, ContainerVolumeDropValidator} from "./container-volume-drop";
import {MessageEntity} from "./message";
import {ServiceWork, ServiceWorkValidator} from "./service-work";

export const DriverActivityValidator = t.intersection([
  t.type({}),
  t.partial({
    person: PersonValidator,
    startShift: ShiftValidator,
    endShift: ShiftValidator,
    pickupTransport: TransportValidator,
    deliveryTransport: TransportValidator,
    transportTransportAssoc: TransportAssocValidator,
    serviceWorkStart: ServiceWorkValidator,
    serviceWorkEnd: ServiceWorkValidator,
    geofenceEvent: GeofenceEventValidator,
    // TODO: driver logs?
    dispatch: DispatchValidator,
    containerVolumeDrop: ContainerVolumeDropValidator,
    warnings: WarningArrayValidator,
  })
]);

export const DriverActivityArrayValidator = t.array(DriverActivityValidator);

export type DriverActivity = t.TypeOf<typeof DriverActivityValidator>;
export type DriverActivityArray = t.TypeOf<typeof DriverActivityArrayValidator>;


@Exclude()
export class DriverActivityEntity {
  @Expose()
  @Type(() => PersonEntity)
  person?: PersonEntity

  @Expose()
  @Type(() => ShiftEntity)
  startShift?: ShiftEntity

  @Expose()
  @Type(() => ShiftEntity)
  endShift?: ShiftEntity

  @Expose()
  @Type(() => TransportEntity)
  pickupTransport?: TransportEntity

  @Expose()
  @Type(() => TransportEntity)
  deliveryTransport?: TransportEntity

  @Expose({name: "transportTransportAssoc"})
  @Type(() => TransportAssocEntity)
  tta?: TransportAssocEntity

  @Expose()
  @Type(() => GeofenceEventEntity)
  geofenceEvent?: GeofenceEventEntity

  @Expose()
  @Type(() => DispatchEntity)
  dispatch?: DispatchEntity

  @Expose()
  @Type(() => ContainerVolumeDropEntity)
  containerVolumeDrop?: ContainerVolumeDropEntity

  @Expose()
  serviceWorkStart?: ServiceWork

  @Expose()
  serviceWorkEnd?: ServiceWork

  @Expose()
  warnings?: Warning[]

  hidden?: boolean

  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(trailer: DriverActivity): DriverActivityEntity {
    return plainToInstance(DriverActivityEntity, trailer, {
      excludeExtraneousValues: true
    });
  }

  loadTypeValue(): Promise<DriverActivity> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(DriverActivityValidator, object);
  }

  asTypeValue(): DriverActivity {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(DriverActivityValidator, object);
  }

  get id() : string | undefined {
    if (this.person) {
      return this.person.id;
    } else if (this.startShift) {
      return this.startShift.id
    } else if (this.endShift) {
      return this.endShift.id
    } else if (this.tta) {
      return this.tta.id
    } else if (this.pickupTransport) {
      return this.pickupTransport.id
    } else if (this.deliveryTransport) {
      return this.deliveryTransport.id
    } else if (this.geofenceEvent) {
      return this.geofenceEvent.id
    } else if (this.dispatch) {
      return this.dispatch.id
    } else if (this.containerVolumeDrop) {
      return this.containerVolumeDrop.id
    }

    return undefined
  }

  get type() : string | undefined {
    if (this.person) {
      return "person"
    } else if (this.startShift) {
      return "startShift"
    } else if (this.endShift) {
      return "endShift"
    } else if (this.tta) {
      return "tta"
    } else if (this.pickupTransport) {
      return "pickupTransport"
    } else if (this.deliveryTransport) {
      return "deliveryTransport"
    } else if (this.geofenceEvent) {
      return "geofenceEvent"
    } else if (this.dispatch) {
      return "dispatch"
    } else if (this.containerVolumeDrop) {
      return "containerVolumeDrop"
    }

    return undefined
  }

  get shift() : ShiftEntity | undefined {
    return this.startShift || this.endShift
  }

  get transport() : TransportEntity | undefined {
    return this.pickupTransport || this.deliveryTransport
  }

  get personId() : string | undefined {
    if (this.person) {
      return this.person.id;
    } else if (this.startShift) {
      return this.startShift.personId
    } else if (this.endShift) {
      return this.endShift.personId
    } else if (this.tta) {
      return this.tta.pickupTransport?.personId
    } else if (this.pickupTransport) {
      return this.pickupTransport.personId
    } else if (this.deliveryTransport) {
      return this.deliveryTransport.personId
    } else if (this.geofenceEvent) {
      return this.geofenceEvent.personId
    } else if (this.dispatch) {
      return this.dispatch.personId
    }

    return undefined
  }

  get messages() : MessageEntity[] {
    const messages : MessageEntity[] = []

    if (this.tta) {
      messages.push(...this.tta.messages)
    } else if (this?.transport) {
      const transport = this.transport
      if (transport.messages) {
        messages.push(...transport.messages)
      }
    } else if (this?.shift) {
      const shift = this.shift
      if (shift.messages) {
        messages.push(...shift.messages)
      }
    }

    return messages
  }
}
